import { useParams, useNavigate } from 'react-router-dom'
import { usePaymentDetail, usePaymentQueries } from '@/hooks/usePaymentQueries'
import { PaymentDetailCard } from '@/components/payments/PaymentDetailCard'
import { proofsOfDetail } from '@/lib/paymentProof'
import { PaymentProofPanel } from '@/components/payments/PaymentProofPanel'
import { VerificationModal } from '@/components/payments/VerificationModal'
import { RejectionModal } from '@/components/payments/RejectionModal'
import { RefundPaymentDialog } from '@/components/payments/RefundPaymentDialog'
import { usePermissions } from '@/hooks/usePermissions'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ErrorFallback } from '@/components/common/ErrorFallback'
import { PageHeader, type PageHeaderAction } from '@/components/common/page-header'
import { Button } from '@/components/ui/button'
import { Check, X, RefreshCw, Loader2 } from 'lucide-react'
import { isRefundCandidate } from '@/lib/paymentRefundability'
import { useState } from 'react'

export function PaymentDetailPage() {
  const { paymentId } = useParams<{ paymentId: string }>()
  const navigate = useNavigate()
  const { verifyPayment, rejectPayment } = usePaymentQueries()
  const paymentDetail = usePaymentDetail(paymentId || '')
  // Read the roles here, above the early returns below. Called further down — after the loading
  // and error branches — the hook only runs on the renders that get past them, so the hook count
  // grows the moment the payment arrives and React tears the page down with error #310.
  const { hasRole } = usePermissions()

  const [verificationModalOpen, setVerificationModalOpen] = useState(false)
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false)
  const [refundDialogOpen, setRefundDialogOpen] = useState(false)
  // Mirrors RefundPaymentDialog's own mutation state, so the header action can disable itself for
  // the length of the request instead of relying on the dialog's modal overlay to keep it out of
  // reach.
  const [refundPending, setRefundPending] = useState(false)
  // An attach or a remove is running in the proof panel. Verify/Reject/Refund are settled against
  // the proof list the panel shows, so none of them should fire while that list is mid-change.
  const [proofBusy, setProofBusy] = useState(false)

  const handleVerify = (notes: string | undefined, receivedAmount: number) => {
    if (!paymentId) return
    verifyPayment.mutate(
      { paymentId, notes, receivedAmount },
      {
        onSuccess: () => {
          setVerificationModalOpen(false)
          // Optionally navigate back to list
          // navigate('/admin/payments')
        },
      }
    )
  }

  const handleReject = (reason: string) => {
    if (!paymentId) return
    rejectPayment.mutate(
      { paymentId, reason },
      {
        onSuccess: () => {
          setRejectionModalOpen(false)
          // Optionally navigate back to list
          // navigate('/admin/payments')
        },
      }
    )
  }

  if (!paymentId) {
    return (
      <ErrorFallback
        error={new Error('Payment ID is required')}
        onRetry={() => navigate('/admin/payments')}
      />
    )
  }

  if (paymentDetail.isLoading) {
    return <LoadingSpinner />
  }

  if (paymentDetail.error && !paymentDetail.data) {
    // Retry before the way out: a detail screen's error state needs Retry first, or the only
    // recovery is walking back to the list to find the same row again. Gated on `!data` too:
    // every write on this page now awaits a detail refetch (including on its own error path, see
    // `usePaymentQueries`'s `onSettled`), and a refetch that fails must not blow away an already
    // loaded payment — the open Verify/Reject/Refund dialog and its BusyOverlay included.
    return (
      <div className="space-y-4">
        <ErrorFallback
          error={paymentDetail.error}
          onRetry={() => paymentDetail.refetch()}
        />
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => navigate('/admin/payments')}>
            Back to payments
          </Button>
        </div>
      </div>
    )
  }

  if (!paymentDetail.data) {
    // Not an ErrorFallback here: there is nothing to retry — the row genuinely does not exist —
    // so the only affordance is the way back, and its label should say that plainly rather than
    // "Try again" (ErrorFallback's own wording, which promises a retry that would just repeat).
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
        <p className="text-muted-foreground">Payment not found</p>
        <Button variant="outline" onClick={() => navigate('/admin/payments')}>
          Back to payments
        </Button>
      </div>
    )
  }

  const payment = paymentDetail.data
  const canSettle = hasRole(['SuperAdmin', 'BillingAdmin'])
  const canRefund = hasRole('SuperAdmin')

  const status = payment.status

  const actions: PageHeaderAction[] = []
  // Verify and reject are SuperAdmin+BillingAdmin; the refund below is SuperAdmin only.
  if (canSettle && (status === 'Pending' || status === 'AwaitingVerification')) {
    actions.push(
      {
        label: 'Verify Payment',
        onClick: () => setVerificationModalOpen(true),
        disabled: verifyPayment.isPending || rejectPayment.isPending || proofBusy,
        icon: <Check className="mr-2 h-4 w-4" />,
      },
      {
        label: 'Reject Payment',
        onClick: () => setRejectionModalOpen(true),
        variant: 'destructive',
        destructive: true,
        disabled: verifyPayment.isPending || rejectPayment.isPending || proofBusy,
        icon: <X className="mr-2 h-4 w-4" />,
      },
    )
  }
  // Shown or hidden by one shared rule; the preview is what actually decides — see
  // isRefundCandidate.
  const refundable = isRefundCandidate({
    status,
    paymentMethod: payment.paymentMethod,
    orderType: payment.order?.type,
    hasOrder: Boolean(payment.order),
  })

  // Nothing was ever submitted for these: a trial settles against the platform's own credit and a
  // balance settlement takes the money straight off the wallet, both born Completed with no step
  // where a customer could attach a slip. The panel offered "No payment proof has been uploaded"
  // plus an Upload button for a receipt that cannot exist.
  const settlesWithoutProof =
    payment.paymentMethod === 'Trial' || payment.paymentMethod === 'Balance'

  if (refundable && canRefund) {
    actions.push({
      label: 'Refund Payment',
      onClick: () => setRefundDialogOpen(true),
      variant: 'outline',
      destructive: true,
      disabled: refundPending || proofBusy,
      icon: <RefreshCw className="mr-2 h-4 w-4" />,
    })
  }

  return (
    <>
      <div className="space-y-6">
        <PageHeader
          title="Payment Details"
          subtitle="View and manage payment information"
          actions={actions}
          back={{ label: 'Back to Payments', onClick: () => navigate('/admin/payments') }}
        />

        {/* A refetch failed but we still have the last good read (`paymentDetail.data`) to show —
            report it here, above the content, rather than tearing the page down to the full-screen
            ErrorFallback: that would unmount the open Verify/Reject/Refund dialog mid-request along
            with everything else on the page. */}
        {paymentDetail.error && (
          <div className="flex items-center justify-between gap-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
            <span className="text-destructive-ink">
              Could not refresh this payment. Showing the last data loaded.
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => paymentDetail.refetch()}
              disabled={paymentDetail.isFetching}
            >
              {paymentDetail.isFetching && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Retry
            </Button>
          </div>
        )}

        {/* Facts on the left, the slips in their own column so an operator can read both
            without scrolling past the proof. */}
        <div
          className={
            settlesWithoutProof
              ? 'grid items-start gap-4'
              : 'grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]'
          }
        >
          <PaymentDetailCard payment={payment} showProof={false} />
          {!settlesWithoutProof && (
            <PaymentProofPanel
              payment={payment}
              className="lg:sticky lg:top-4"
              onBusyChange={setProofBusy}
            />
          )}
        </div>
      </div>

      <VerificationModal
        open={verificationModalOpen}
        onOpenChange={setVerificationModalOpen}
        payment={{
          paymentOrderNumber: payment.paymentOrderNumber,
          amount: payment.amount,
          currency: payment.currency,
          qrProvider: payment.qrProvider,
          proofs: proofsOfDetail(payment),
        }}
        onConfirm={handleVerify}
        isLoading={verifyPayment.isPending}
      />

      <RejectionModal
        open={rejectionModalOpen}
        onOpenChange={setRejectionModalOpen}
        onConfirm={handleReject}
        isLoading={rejectPayment.isPending}
      />

      {refundable && (
        <RefundPaymentDialog
          paymentId={payment.paymentId}
          paymentAmount={payment.amount}
          paymentCurrency={payment.currency}
          open={refundDialogOpen}
          onOpenChange={setRefundDialogOpen}
          onPendingChange={setRefundPending}
        />
      )}
    </>
  )
}
